import { IPromise } from 'angular';
import { put } from 'redux-saga/effects';

import { Messages } from '../../../../codegen/Localize';
import { ListResult } from '../../../../ts/fetchers/ListResult';
import { Filter } from '../../../../ts/helpers/Filter';
import {
  DataRoleDefinition,
  DatasourceDefinition,
  FlowDefinition,
  MetricDefinition,
  ProjectDefinition,
  PublishedConnectionDefinition,
  ViewDefinition,
  WorkbookDefinition,
} from '../../../../ts/models/ContentDefinitions';
import { FlowBasicInfo } from '../../../../ts/models/FlowInfo';
import { IDataConnectionInfo } from '../../../../ts/models/IDataConnectionInfo';
import { IDataRoleDetailInfo } from '../../../../ts/models/IDataRoleInfo';
import { IDataSourceInfo } from '../../../../ts/models/IDataSourceInfo';
import { IMetricInfo } from '../../../../ts/models/IMetricInfo';
import { IPublishedConnectionInfo } from '../../../../ts/models/IPublishedConnectionInfo';
import { IViewDetailInfo } from '../../../../ts/models/IViewInfo';
import { IWorkbookInfo } from '../../../../ts/models/IWorkbookInfo';
import { IExplorableItemTypeInfo } from '../../../../ts/models/resourceInfoParts';
import { DataConnections } from '../../../../ts/resources/DataConnections';
import { DataRoles } from '../../../../ts/resources/Dataroles';
import { DataSources } from '../../../../ts/resources/Datasources';
import { Flows } from '../../../../ts/resources/Flows';
import { Metrics } from '../../../../ts/resources/Metrics';
import { PublishedConnections } from '../../../../ts/resources/PublishedConnections';
import { Views } from '../../../../ts/resources/Views';
import { Workbooks } from '../../../../ts/resources/Workbooks';
import { requestFailed } from '../../../actions/RequestFailedAction';
import {
  GetContentDetailAction,
  updateContentDetail,
} from '../../../actions/spaces/GetContentDetailAction';
import { ServiceLocator } from '../../../ServiceLocator';

export function* processGetContentDetailAction(action: GetContentDetailAction) {
  try {
    switch(action.content.contentType){
      case ProjectDefinition.contentType:
        break;
      case WorkbookDefinition.contentType:
        let workbookContent = null;
        if (!Workbooks.isWorkbookInfo(action.content)) {
          // getting workbook info instead of workbook detail info, because numSheets does not exist in workbook detail info
          workbookContent = getWorkbookInfo(action.content);
        }

        let datasourceList = null;
        if(!action.content.hasOwnProperty('datasources')){
          datasourceList = getDatasourcesForWorkbook(action.content.id);
        }

        let updatedContent: any = null;
        yield Promise.all([workbookContent, datasourceList]).then(function(results){
          if(results[0]){
            updatedContent = results[0];
          }
          if(results[1]){
            updatedContent = updatedContent || action.content;
            updatedContent = { ...updatedContent, datasources: results[1].items };
          }
        });
        //if anything has changed, update the react state
        if(updatedContent){
          yield put(updateContentDetail(updatedContent));
        }
        break;
      case ViewDefinition.contentType:
        if (!Views.isViewDetailInfo(action.content)) {
          const viewDetailInfo = yield getViewDetailInfo(action.content);
          yield put(updateContentDetail(viewDetailInfo));
        }
        break;
      case DatasourceDefinition.contentType:
        const ds = action.content as any;
        if (!ds.connections) {
          const connections = yield getDataConnections(action.content);
          let project = null;
          if(connections.length > 0 && connections[0].datasource) {
            project = connections[0].datasource.project;
          }
          const updatedContent = project ? { ...action.content, connections, project } : { ...action.content, connections};
          yield put(updateContentDetail(updatedContent));
        }
        break;
      case DataRoleDefinition.contentType:
        if(!DataRoles.isDataRoleDetailedInfo(action.content)) {
          const dataRoleInfo = yield getDataRoleDetailInfo(action.content);
          yield put(updateContentDetail(dataRoleInfo));
        }
        break;
      case MetricDefinition.contentType:
        if (!Metrics.isMetricInfo(action.content)) {
          const metricInfo = yield getMetricInfo(action.content);
          yield put(updateContentDetail(metricInfo));
        }
        break;
      case FlowDefinition.contentType:
        if(!Flows.isFlowInfo(action.content)) {
          const flowInfo = yield getFlowInfo(action.content);
          yield put(updateContentDetail(flowInfo));
        }
        break;
      case PublishedConnectionDefinition.contentType:
        if (!PublishedConnections.isPublishedConnectionInfo(action.content)) {
          const publishedConnectionInfo = yield getPublishedConnectionInfo(action.content);
          yield put(updateContentDetail(publishedConnectionInfo));
        }
        break;
    }
  }
  catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getViewDetailInfo(content: IExplorableItemTypeInfo): IPromise<IViewDetailInfo> {
  const resource = ServiceLocator.get<Views>('Views');
  return resource.getById(content.id);
}

export function getDataRoleDetailInfo(content: IExplorableItemTypeInfo): IPromise<IDataRoleDetailInfo> {
  const resource = ServiceLocator.get<DataRoles>('DataRoles');
  return resource.getById(content.id);
}

export function getFlowInfo(content: IExplorableItemTypeInfo): IPromise<FlowBasicInfo> {
  const resource = ServiceLocator.get<Flows>('Flows');
  return resource.getById(content.id);
}

export function getPublishedConnectionInfo(content: IExplorableItemTypeInfo): IPromise<IPublishedConnectionInfo> {
  const resource = ServiceLocator.get<PublishedConnections>('PublishedConnections');
  return resource.getById(content.id);
}

export function getMetricInfo(content: IExplorableItemTypeInfo): IPromise<IMetricInfo> {
  const resource = ServiceLocator.get<Metrics>('Metrics');
  return resource.getById(content.id);
}

function getWorkbookInfo(content: IExplorableItemTypeInfo): IPromise<IWorkbookInfo> {
  const resource = ServiceLocator.get<Workbooks>('Workbooks');
  return resource.all().addFilter(Filter.stringFieldEq('id', content.id)).getSlice(0, 1).then(result => result.items[0]);
}

function getDataConnections(content: IExplorableItemTypeInfo): IPromise<IDataConnectionInfo[]> {
  const datasources = ServiceLocator.get<DataSources>(DataSources.ServiceLocatorKey);
  return datasources.fromContent(content).then(datasourceInfos => {
    if (datasourceInfos.length > 0) {
      const connections = ServiceLocator.get<DataConnections>('DataConnections');
      return connections.getFromDatasource(datasourceInfos[0]);
    } else {
      return [];
    }
  });
}

function getDatasourcesForWorkbook(workbookId: string): IPromise<ListResult<IDataSourceInfo>> {
  const datasources = ServiceLocator.get<DataSources>(DataSources.ServiceLocatorKey);
  return datasources.getByWorkbookId(workbookId);
}
